/**
 * System Admin API Client
 *
 * Dedicated axios instance for the /admin/* endpoints, entirely separate
 * from the tenant RealAPIClient/shared singleton in ./client.ts and
 * ./index.ts. System admin tokens and tenant tokens must never be sent to
 * each other's requests.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { saveAdminAuthToken, clearAdminAuthStorage } from '../utils/adminStorage';

export interface AdminInfo {
  id: string;
  email: string;
}

export interface AdminLoginResponse {
  token: string;
  admin: AdminInfo;
}

export type TenantStatus = 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';

export type TenantUserRole = 'ADMIN' | 'OFFICE_MANAGER' | 'OFFICE' | 'TECHNICIAN' | 'CUSTOMER_PORTAL';

export interface TenantCounts {
  customers: number;
  users: number;
  jobs: number;
  invoices: number;
}

export interface TenantSummary {
  id: string;
  name: string;
  displayName: string;
  slug: string;
  status: TenantStatus;
  createdAt: string;
  updatedAt: string;
  counts: TenantCounts;
}

export interface CreateTenantRequest {
  name: string;
  displayName: string;
  slug: string;
}

export interface UpdateTenantRequest {
  displayName?: string;
  status?: TenantStatus;
}

export interface TenantUserSummary {
  id: string;
  email: string;
  role: TenantUserRole;
  isActive: boolean;
  createdAt: string;
}

export interface CreateTenantUserRequest {
  email: string;
  password: string;
  role: TenantUserRole;
}

export interface UpdateTenantUserRequest {
  role?: TenantUserRole;
  isActive?: boolean;
}

interface APIEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

const LOGIN_PATH = '/admin/auth/login';

class AdminApiClient {
  private httpClient: AxiosInstance;
  private token: string | null = null;

  constructor(baseURL: string = process.env.REACT_APP_API_URL || 'http://localhost:5000/api') {
    this.httpClient = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.httpClient.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.httpClient.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const isLoginRequest = error.config?.url === LOGIN_PATH;
        if (error.response?.status === 401 && !isLoginRequest) {
          this.clearAdminToken();
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setAdminToken(token: string): void {
    this.token = token;
    saveAdminAuthToken(token);
  }

  getAdminToken(): string | null {
    return this.token;
  }

  clearAdminToken(): void {
    this.token = null;
    clearAdminAuthStorage();
  }

  /** Restore a token already persisted in localStorage (app init). */
  restoreAdminToken(token: string): void {
    this.token = token;
  }

  async adminLogin(email: string, password: string): Promise<AdminLoginResponse> {
    const response = await this.httpClient.post<APIEnvelope<AdminLoginResponse>>(LOGIN_PATH, {
      email,
      password,
    });
    return response.data.data;
  }

  async listTenants(): Promise<TenantSummary[]> {
    const response = await this.httpClient.get<APIEnvelope<TenantSummary[]>>('/admin/tenants');
    return response.data.data;
  }

  async createTenant(data: CreateTenantRequest): Promise<TenantSummary> {
    const response = await this.httpClient.post<APIEnvelope<TenantSummary>>('/admin/tenants', data);
    return response.data.data;
  }

  async getTenant(id: string): Promise<TenantSummary> {
    const response = await this.httpClient.get<APIEnvelope<TenantSummary>>(`/admin/tenants/${id}`);
    return response.data.data;
  }

  async updateTenant(id: string, data: UpdateTenantRequest): Promise<TenantSummary> {
    const response = await this.httpClient.patch<APIEnvelope<TenantSummary>>(`/admin/tenants/${id}`, data);
    return response.data.data;
  }

  async listTenantUsers(tenantId: string): Promise<TenantUserSummary[]> {
    const response = await this.httpClient.get<APIEnvelope<TenantUserSummary[]>>(
      `/admin/tenants/${tenantId}/users`
    );
    return response.data.data;
  }

  async createTenantUser(tenantId: string, data: CreateTenantUserRequest): Promise<TenantUserSummary> {
    const response = await this.httpClient.post<APIEnvelope<TenantUserSummary>>(
      `/admin/tenants/${tenantId}/users`,
      data
    );
    return response.data.data;
  }

  async updateTenantUser(
    tenantId: string,
    userId: string,
    data: UpdateTenantUserRequest
  ): Promise<TenantUserSummary> {
    const response = await this.httpClient.patch<APIEnvelope<TenantUserSummary>>(
      `/admin/tenants/${tenantId}/users/${userId}`,
      data
    );
    return response.data.data;
  }

  async resetTenantUserPassword(tenantId: string, userId: string, newPassword: string): Promise<void> {
    await this.httpClient.post(`/admin/tenants/${tenantId}/users/${userId}/reset-password`, {
      newPassword,
    });
  }
}

// Module-level singleton - every consumer (context, pages) must share the
// same instance so the bearer token set at login is visible everywhere.
export const adminClient = new AdminApiClient();
export default adminClient;
